import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaDatabase,
  FaCode,
  FaCalculator,
  FaAtom,
  FaCheck,
  FaBolt,
  FaChartBar,
  FaExclamationCircle,
  FaArrowRight,
  FaBookmark,
  FaChevronLeft,
  FaChevronRight,
  FaList,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints.js';
import {
  isTheoryBookmarked,
  toggleTheoryBookmark,
} from '../utils/theoryBookmarks.ts';
import {
  markLessonCompleted,
  markSublessonReached,
  readCompletedLessons,
  readSublessonProgress,
} from '../utils/theoryProgress.ts';
import { SuggestTopicModal } from '../components/SuggestTopicModal.tsx';
import { TheoryPracticeModal } from '../components/TheoryPracticeModal.tsx';
import { TheoryArticleBody } from '../components/TheoryArticleBody.tsx';
import { getTheoryArticleSections } from '../content/theoryArticles.tsx';
import './css/TheoryPage.css';

interface TheorySublesson {
  id: number;
  slug: string;
  title: string;
}

interface TheoryLesson {
  id: number;
  slug: string;
  title: string;
  practice_problem_ids?: number[];
  sublessons: TheorySublesson[];
}

interface TheoryCategory {
  id: number;
  slug: string;
  title: string;
  icon: string;
  lessons: TheoryLesson[];
}

const COPY = {
  ru: {
    searchPlaceholder: 'Поиск в теории',
    libraryTitle: 'Библиотека теории',
    libraryLoading: 'Загрузка библиотеки…',
    libraryLoadError: 'Не удалось загрузить библиотеку теории',
    libraryEmpty: 'Пока нет доступных тем',
    suggestTitle: 'Не нашли нужную тему?',
    suggestText: 'Предложите тему — мы её добавим.',
    suggestBtn: 'Предложить тему',
    lessonStructure: 'Структура урока',
    progress: 'Прогресс',
    keyFacts: 'Ключевые факты',
    breadcrumbTheory: 'Теория',
    articleDesc: 'Масштабирование признаков для стабильного обучения моделей.',
    articleEmpty: 'Материал для этой темы скоро появится.',
    goPractice: 'Перейти к практике',
    bookmarkAdd: 'Добавить в заметки',
    bookmarkRemove: 'Убрать из заметок',
    bookmarkLoginRequired: 'Войдите, чтобы добавить в заметки',
    prevTopic: 'Предыдущая тема',
    backToList: 'Вернуться к списку',
    collapseNav: 'Свернуть навигацию',
    expandNav: 'Показать навигацию',
    nextTopic: 'Следующая тема',
    completeTopic: 'Завершить тему',
    facts: [
      'Нормализация выравнивает масштаб признаков и ускоряет сходимость градиентных методов.',
      'Min-Max и Z-score — самые частые подходы на практике.',
      'Параметры scaler нужно обучать только на train, иначе возникает data leakage.',
    ],
  },
  en: {
    searchPlaceholder: 'Search theory',
    libraryTitle: 'Theory library',
    libraryLoading: 'Loading library…',
    libraryLoadError: 'Failed to load theory library',
    libraryEmpty: 'No topics available yet',
    suggestTitle: "Can't find a topic?",
    suggestText: 'Suggest a topic — we will add it.',
    suggestBtn: 'Suggest a topic',
    lessonStructure: 'Lesson structure',
    progress: 'Progress',
    keyFacts: 'Key facts',
    breadcrumbTheory: 'Theory',
    articleDesc: 'Feature scaling for stable model training.',
    articleEmpty: 'Content for this topic is coming soon.',
    goPractice: 'Go to practice',
    bookmarkAdd: 'Add to notes',
    bookmarkRemove: 'Remove from notes',
    bookmarkLoginRequired: 'Sign in to add to notes',
    prevTopic: 'Previous topic',
    backToList: 'Back to list',
    collapseNav: 'Collapse navigation',
    expandNav: 'Show navigation',
    nextTopic: 'Next topic',
    completeTopic: 'Complete topic',
    facts: [
      'Normalization aligns feature scales and speeds up gradient-based convergence.',
      'Min-Max and Z-score are the most common approaches in practice.',
      'Fit scaler parameters on train only to avoid data leakage.',
    ],
  },
} as const;

const LIBRARY_CATEGORY_ORDER = ['algorithms', 'sql', 'math', 'physics'] as const;
const LEFT_NAV_COLLAPSED_KEY = 'nn_theory_nav_collapsed';

function readLeftNavCollapsed(): boolean {
  try {
    return localStorage.getItem(LEFT_NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLeftNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(LEFT_NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function getPlaceholderCategories(lang: 'ru' | 'en'): TheoryCategory[] {
  const titles =
    lang === 'ru'
      ? {
          algorithms: 'Алгоритмы',
          math: 'Математика',
          physics: 'Физика',
        }
      : {
          algorithms: 'Algorithms',
          math: 'Mathematics',
          physics: 'Physics',
        };

  return [
    {
      id: -1,
      slug: 'algorithms',
      title: titles.algorithms,
      icon: 'code',
      lessons: [],
    },
    {
      id: -2,
      slug: 'math',
      title: titles.math,
      icon: 'math',
      lessons: [],
    },
    {
      id: -3,
      slug: 'physics',
      title: titles.physics,
      icon: 'physics',
      lessons: [],
    },
  ];
}

function mergeLibraryCategories(
  apiCategories: TheoryCategory[],
  lang: 'ru' | 'en'
): TheoryCategory[] {
  const bySlug = new Map(apiCategories.map((category) => [category.slug, category]));

  for (const placeholder of getPlaceholderCategories(lang)) {
    if (!bySlug.has(placeholder.slug)) {
      bySlug.set(placeholder.slug, placeholder);
    }
  }

  return LIBRARY_CATEGORY_ORDER.map((slug) => bySlug.get(slug)).filter(
    (category): category is TheoryCategory => category != null
  );
}

function getCategoryIcon(icon: string) {
  switch (icon) {
    case 'database':
      return <FaDatabase aria-hidden />;
    case 'code':
      return <FaCode aria-hidden />;
    case 'math':
      return <FaCalculator aria-hidden />;
    case 'physics':
      return <FaAtom aria-hidden />;
    default:
      return <FaDatabase aria-hidden />;
  }
}

function filterLibrary(categories: TheoryCategory[], query: string): TheoryCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;

  return categories
    .map((category) => {
      const lessons = category.lessons
        .map((lesson) => {
          const lessonMatches = lesson.title.toLowerCase().includes(q);
          const sublessons = lesson.sublessons.filter(
            (sublesson) =>
              lessonMatches || sublesson.title.toLowerCase().includes(q)
          );
          if (!lessonMatches && sublessons.length === 0) return null;
          return {
            ...lesson,
            sublessons: lessonMatches ? lesson.sublessons : sublessons,
          };
        })
        .filter((lesson): lesson is TheoryLesson => lesson != null);

      if (lessons.length === 0) return null;
      return { ...category, lessons };
    })
    .filter((category): category is TheoryCategory => category != null);
}

function findSelection(
  categories: TheoryCategory[],
  lessonId: number | null,
  sublessonId: number | null
) {
  if (lessonId == null) return null;

  for (const category of categories) {
    const lesson = category.lessons.find((item) => item.id === lessonId);
    if (!lesson) continue;

    const sublesson =
      sublessonId != null
        ? lesson.sublessons.find((item) => item.id === sublessonId)
        : lesson.sublessons[0];

    return { category, lesson, sublesson: sublesson ?? null };
  }

  return null;
}

type SublessonRef = {
  categorySlug: string;
  lessonId: number;
  sublessonId: number;
};

function buildSublessonTrail(categories: TheoryCategory[]): SublessonRef[] {
  const trail: SublessonRef[] = [];

  for (const category of categories) {
    for (const lesson of category.lessons) {
      for (const sublesson of lesson.sublessons) {
        trail.push({
          categorySlug: category.slug,
          lessonId: lesson.id,
          sublessonId: sublesson.id,
        });
      }
    }
  }

  return trail;
}

function findSublessonIndex(
  trail: SublessonRef[],
  lessonId: number | null,
  sublessonId: number | null
): number {
  if (lessonId == null || sublessonId == null) return -1;
  return trail.findIndex(
    (item) => item.lessonId === lessonId && item.sublessonId === sublessonId
  );
}

type LessonRef = {
  categorySlug: string;
  lessonId: number;
  sublessonId: number | null;
};

function buildLessonTrail(categories: TheoryCategory[]): LessonRef[] {
  const trail: LessonRef[] = [];

  for (const category of categories) {
    for (const lesson of category.lessons) {
      trail.push({
        categorySlug: category.slug,
        lessonId: lesson.id,
        sublessonId: lesson.sublessons[0]?.id ?? null,
      });
    }
  }

  return trail;
}

function findLessonIndex(trail: LessonRef[], lessonId: number | null): number {
  if (lessonId == null) return -1;
  return trail.findIndex((item) => item.lessonId === lessonId);
}

function resolveLessonFromParams(
  categories: TheoryCategory[],
  searchParams: URLSearchParams
): {
  categorySlug: string;
  lessonId: number;
  sublessonId: number | null;
} | null {
  const lessonIdParam = searchParams.get('lessonId');
  if (lessonIdParam) {
    const lessonId = Number(lessonIdParam);
    if (!Number.isFinite(lessonId)) return null;

    for (const category of categories) {
      const lesson = category.lessons.find((item) => item.id === lessonId);
      if (lesson) {
        return {
          categorySlug: category.slug,
          lessonId: lesson.id,
          sublessonId: lesson.sublessons[0]?.id ?? null,
        };
      }
    }
    return null;
  }

  const categorySlug = searchParams.get('category');
  const lessonSlug = searchParams.get('lesson');
  if (!categorySlug || !lessonSlug) return null;

  const category = categories.find((item) => item.slug === categorySlug);
  const lesson = category?.lessons.find((item) => item.slug === lessonSlug);
  if (!category || !lesson) return null;

  return {
    categorySlug: category.slug,
    lessonId: lesson.id,
    sublessonId: lesson.sublessons[0]?.id ?? null,
  };
}

function TheoryPage() {
  const [searchParams] = useSearchParams();
  const { currentLanguage } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const copy = COPY[currentLanguage === 'ru' ? 'ru' : 'en'];
  const lang = currentLanguage === 'ru' ? 'ru' : 'en';

  const [library, setLibrary] = useState<TheoryCategory[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(() => new Set());
  const [activeLessonId, setActiveLessonId] = useState<number | null>(null);
  const [activeSublessonId, setActiveSublessonId] = useState<number | null>(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [completedLessons, setCompletedLessons] = useState<Set<number>>(() => new Set());
  const [solvedProblemIds, setSolvedProblemIds] = useState<Set<number>>(() => new Set());
  const [sublessonProgress, setSublessonProgress] = useState<Map<number, number>>(
    () => new Map()
  );
  const [leftNavCollapsed, setLeftNavCollapsed] = useState(() => readLeftNavCollapsed());

  const toggleLeftNav = (collapsed: boolean) => {
    setLeftNavCollapsed(collapsed);
    writeLeftNavCollapsed(collapsed);
  };

  const handleBackToList = () => {
    toggleLeftNav(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const lessonPracticeProblems = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const category of library) {
      for (const lesson of category.lessons) {
        map.set(lesson.id, lesson.practice_problem_ids ?? []);
      }
    }
    return map;
  }, [library]);

  const isLessonComplete = useCallback(
    (lessonId: number) => {
      const practiceProblemIds = lessonPracticeProblems.get(lessonId) ?? [];
      if (practiceProblemIds.length === 0) {
        return completedLessons.has(lessonId);
      }
      if (!isAuthenticated) return false;
      return practiceProblemIds.every((problemId) => solvedProblemIds.has(problemId));
    },
    [lessonPracticeProblems, completedLessons, isAuthenticated, solvedProblemIds]
  );

  const loadSolvedProblemIds = useCallback(async () => {
    if (!isAuthenticated) {
      setSolvedProblemIds(new Set());
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/theory/solved-problem-ids`, {
        credentials: 'include',
      });
      if (!response.ok) return;

      const data = (await response.json()) as { problem_ids?: number[] };
      const ids = Array.isArray(data.problem_ids)
        ? data.problem_ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
        : [];
      setSolvedProblemIds(new Set(ids));
    } catch {
      setSolvedProblemIds(new Set());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const userId = user?.id ?? null;
    setCompletedLessons(readCompletedLessons(userId));
    setSublessonProgress(readSublessonProgress(userId));
  }, [user?.id]);

  useEffect(() => {
    loadSolvedProblemIds();
  }, [loadSolvedProblemIds]);

  useEffect(() => {
    const onFocus = () => {
      loadSolvedProblemIds();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadSolvedProblemIds]);

  useEffect(() => {
    let cancelled = false;

    const loadLibrary = async () => {
      setLibraryLoading(true);
      setLibraryError(null);

      try {
        const response = await fetch(`${API_BASE}/api/theory/library?lang=${lang}`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('library fetch failed');
        }

        const data = (await response.json()) as { categories: TheoryCategory[] };
        if (cancelled) return;

        const categories = mergeLibraryCategories(
          Array.isArray(data.categories) ? data.categories : [],
          lang
        );
        setLibrary(categories);

        const fromParams = resolveLessonFromParams(categories, searchParams);
        const firstCategoryWithLessons = categories.find((category) => category.lessons.length > 0);
        const firstLesson = firstCategoryWithLessons?.lessons[0];
        const initialSelection = fromParams ?? (firstLesson && firstCategoryWithLessons
          ? {
              categorySlug: firstCategoryWithLessons.slug,
              lessonId: firstLesson.id,
              sublessonId: firstLesson.sublessons[0]?.id ?? null,
            }
          : null);

        if (initialSelection) {
          setExpandedCategory(initialSelection.categorySlug);
          setExpandedLessons(new Set([initialSelection.lessonId]));
          setActiveLessonId(initialSelection.lessonId);
          setActiveSublessonId(initialSelection.sublessonId);
        } else if (firstCategoryWithLessons) {
          setExpandedCategory(firstCategoryWithLessons.slug);
        } else if (categories[0]) {
          setExpandedCategory(categories[0].slug);
        }
      } catch {
        if (!cancelled) {
          setLibrary([]);
          setLibraryError(copy.libraryLoadError);
        }
      } finally {
        if (!cancelled) {
          setLibraryLoading(false);
        }
      }
    };

    loadLibrary();

    return () => {
      cancelled = true;
    };
  }, [lang, copy.libraryLoadError, searchParams]);

  const filteredLibrary = useMemo(
    () => filterLibrary(library, searchQuery),
    [library, searchQuery]
  );

  const selection = useMemo(
    () => findSelection(library, activeLessonId, activeSublessonId),
    [library, activeLessonId, activeSublessonId]
  );

  const lessonTrail = useMemo(() => buildLessonTrail(library), [library]);
  const sublessonTrail = useMemo(() => buildSublessonTrail(library), [library]);

  const lessonSteps = selection?.lesson.sublessons ?? [];
  const currentStepIndex = lessonSteps.findIndex(
    (step) => step.id === activeSublessonId
  );
  const currentSublessonIndex = findSublessonIndex(
    sublessonTrail,
    activeLessonId,
    activeSublessonId
  );
  const currentLessonIndex = findLessonIndex(lessonTrail, activeLessonId);
  const hasPrevSublesson = currentSublessonIndex > 0;
  const hasNextSublesson =
    currentSublessonIndex >= 0 && currentSublessonIndex < sublessonTrail.length - 1;
  const isLastSublessonInLesson =
    currentStepIndex >= 0 && currentStepIndex === lessonSteps.length - 1;
  const hasNextLesson =
    currentLessonIndex >= 0 && currentLessonIndex < lessonTrail.length - 1;
  const showCompleteTopic = isLastSublessonInLesson && lessonSteps.length > 0;

  const lessonTotal = lessonSteps.length;
  const isLessonCompleted =
    activeLessonId != null && isLessonComplete(activeLessonId);
  const furthestSublessonIndex =
    activeLessonId != null ? (sublessonProgress.get(activeLessonId) ?? -1) : -1;

  const lessonProgress = isLessonCompleted
    ? lessonTotal
    : Math.min(
        lessonTotal,
        Math.max(
          furthestSublessonIndex >= 0 ? furthestSublessonIndex + 1 : 0,
          currentStepIndex >= 0 ? currentStepIndex + 1 : 0,
          lessonSteps.length > 0 ? 1 : 0
        )
      );
  const progressPct = lessonTotal > 0 ? (lessonProgress / lessonTotal) * 100 : 0;

  const activeBookmarkId = selection
    ? `${selection.category.slug}/${selection.lesson.slug}/${selection.sublesson?.slug ?? ''}`
    : '';

  const activeBookmarkTitle =
    selection?.sublesson?.title ?? selection?.lesson.title ?? '';

  const articleSections = useMemo(
    () => getTheoryArticleSections(selection?.sublesson?.slug),
    [selection?.sublesson?.slug]
  );

  const articleTitle =
    selection?.sublesson?.title ?? selection?.lesson.title ?? '—';

  const articleSubtitle = articleSections?.subtitle ?? copy.articleDesc;

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !activeBookmarkId) {
      setBookmarked(false);
      return;
    }
    setBookmarked(isTheoryBookmarked(user.id, activeBookmarkId));
  }, [isAuthenticated, user?.id, activeBookmarkId]);

  const toggleCategory = (slug: string) => {
    setExpandedCategory((prev) => (prev === slug ? prev : slug));
  };

  const rememberSublessonProgress = (lessonId: number, sublessonId: number) => {
    const lesson = library
      .flatMap((category) => category.lessons)
      .find((item) => item.id === lessonId);
    const sublessonIndex =
      lesson?.sublessons.findIndex((sublesson) => sublesson.id === sublessonId) ?? -1;

    if (sublessonIndex < 0) return;

    const userId = user?.id ?? null;
    markSublessonReached(userId, lessonId, sublessonIndex);
    setSublessonProgress((prev) => {
      const current = prev.get(lessonId) ?? -1;
      if (sublessonIndex <= current) return prev;

      const next = new Map(prev);
      next.set(lessonId, sublessonIndex);
      return next;
    });
  };

  const handleLessonClick = (lesson: TheoryLesson) => {
    const isOnlyOpenLesson =
      expandedLessons.has(lesson.id) && expandedLessons.size === 1;
    const firstSublessonId = lesson.sublessons[0]?.id;

    if (firstSublessonId != null) {
      rememberSublessonProgress(lesson.id, firstSublessonId);
    }

    setActiveLessonId(lesson.id);
    setActiveSublessonId(firstSublessonId ?? null);
    setExpandedLessons(isOnlyOpenLesson ? new Set() : new Set([lesson.id]));
  };

  const handleSelectSublesson = (lessonId: number, sublessonId: number) => {
    rememberSublessonProgress(lessonId, sublessonId);
    setActiveLessonId(lessonId);
    setActiveSublessonId(sublessonId);
    setExpandedLessons(new Set([lessonId]));
  };

  const navigateToSublesson = (ref: SublessonRef) => {
    rememberSublessonProgress(ref.lessonId, ref.sublessonId);

    setExpandedCategory(ref.categorySlug);
    setExpandedLessons(new Set([ref.lessonId]));
    setActiveLessonId(ref.lessonId);
    setActiveSublessonId(ref.sublessonId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToLesson = (ref: LessonRef) => {
    if (ref.sublessonId == null) return;
    navigateToSublesson({
      categorySlug: ref.categorySlug,
      lessonId: ref.lessonId,
      sublessonId: ref.sublessonId,
    });
  };

  const handlePrevTopic = () => {
    if (!hasPrevSublesson) return;
    navigateToSublesson(sublessonTrail[currentSublessonIndex - 1]);
  };

  const handleNextTopic = () => {
    if (!hasNextSublesson) return;
    navigateToSublesson(sublessonTrail[currentSublessonIndex + 1]);
  };

  const handleCompleteTopic = () => {
    if (!showCompleteTopic || activeLessonId == null) return;

    const userId = user?.id ?? null;
    const lastSublessonIndex = lessonSteps.length - 1;
    const practiceProblemIds = lessonPracticeProblems.get(activeLessonId) ?? [];
    const hasPracticeProblems = practiceProblemIds.length > 0;

    if (!hasPracticeProblems) {
      markLessonCompleted(userId, activeLessonId);
      setCompletedLessons((prev) => new Set(prev).add(activeLessonId));
    }

    if (lastSublessonIndex >= 0) {
      markSublessonReached(userId, activeLessonId, lastSublessonIndex);
      setSublessonProgress((prev) => {
        const next = new Map(prev);
        next.set(activeLessonId, lastSublessonIndex);
        return next;
      });
    }

    if (hasNextLesson) {
      navigateToLesson(lessonTrail[currentLessonIndex + 1]);
    }
  };

  const handleToggleBookmark = () => {
    if (!isAuthenticated || !user?.id || !activeBookmarkId) return;

    const added = toggleTheoryBookmark(user.id, {
      id: activeBookmarkId,
      title: activeBookmarkTitle,
    });
    setBookmarked(added);
  };

  const bookmarkTitle = !isAuthenticated
    ? copy.bookmarkLoginRequired
    : bookmarked
      ? copy.bookmarkRemove
      : copy.bookmarkAdd;

  const suggestCategories = library.map((category) => ({
    id: category.slug,
    label: category.title,
  }));

  return (
    <div className="theory-page">
      <div
        className={`theory-layout${leftNavCollapsed ? ' theory-layout--nav-collapsed' : ''}`}
      >
        <aside
          className="theory-sidebar theory-sidebar--left"
          aria-hidden={leftNavCollapsed}
        >
          <div className="theory-sidebar-left-head">
            <div className="theory-search">
              <FaSearch className="theory-search-icon" aria-hidden />
              <input
                type="search"
                className="theory-search-input"
                placeholder={copy.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="theory-nav-collapse-btn"
              onClick={() => toggleLeftNav(true)}
              title={copy.collapseNav}
              aria-label={copy.collapseNav}
              aria-expanded={!leftNavCollapsed}
            >
              <FaChevronLeft aria-hidden />
            </button>
          </div>

          <div className="theory-library-card">
            <h2 className="theory-library-title">{copy.libraryTitle}</h2>

            {libraryLoading && (
              <p className="theory-library-status">{copy.libraryLoading}</p>
            )}
            {libraryError && (
              <p className="theory-library-status theory-library-status--error" role="alert">
                {libraryError}
              </p>
            )}
            {!libraryLoading && !libraryError && filteredLibrary.length === 0 && (
              <p className="theory-library-status">{copy.libraryEmpty}</p>
            )}

            <nav className="theory-categories" aria-label={copy.libraryTitle}>
              {filteredLibrary.map((category) => {
                const isOpen = expandedCategory === category.slug;

                return (
                  <div
                    key={category.id}
                    className={`theory-category${isOpen ? ' theory-category--open' : ''}`}
                  >
                    <button
                      type="button"
                      className="theory-category-head"
                      onClick={() => toggleCategory(category.slug)}
                      aria-expanded={isOpen}
                    >
                      <span className="theory-category-icon">
                        {getCategoryIcon(category.icon)}
                      </span>
                      <span className="theory-category-label">{category.title}</span>
                      {isOpen ? (
                        <FaChevronUp className="theory-category-chevron" aria-hidden />
                      ) : (
                        <FaChevronDown className="theory-category-chevron" aria-hidden />
                      )}
                    </button>

                    {isOpen && (
                      <ul className="theory-topic-list">
                        {category.lessons.map((lesson, index) => {
                          const isLessonOpen = expandedLessons.has(lesson.id);
                          const isLessonActive = lesson.id === activeLessonId;
                          const isLessonCompleted = isLessonComplete(lesson.id);

                          return (
                            <li key={lesson.id} className="theory-topic-group">
                              <button
                                type="button"
                                className={`theory-topic-item theory-topic-item--branch${
                                  isLessonOpen ? ' theory-topic-item--branch-open' : ''
                                }${isLessonActive ? ' theory-topic-item--active' : ''}${
                                  isLessonCompleted ? ' theory-topic-item--completed' : ''
                                }`}
                                onClick={() => handleLessonClick(lesson)}
                                aria-expanded={isLessonOpen}
                              >
                                <span className="theory-topic-num">{index + 1}.</span>
                                <span className="theory-topic-text">{lesson.title}</span>
                                {isLessonCompleted && (
                                  <FaCheck
                                    className="theory-topic-complete-icon"
                                    aria-label="Тема завершена"
                                  />
                                )}
                                {isLessonOpen ? (
                                  <FaChevronUp className="theory-topic-chevron" aria-hidden />
                                ) : (
                                  <FaChevronDown className="theory-topic-chevron" aria-hidden />
                                )}
                              </button>
                              {isLessonOpen && (
                                <ul className="theory-topic-sublist">
                                  {lesson.sublessons.map((sublesson, childIndex) => {
                                    const isSubActive =
                                      isLessonActive && sublesson.id === activeSublessonId;
                                    return (
                                      <li key={sublesson.id}>
                                        <button
                                          type="button"
                                          className={`theory-topic-subitem${
                                            isSubActive ? ' theory-topic-subitem--active' : ''
                                          }`}
                                          onClick={() =>
                                            handleSelectSublesson(lesson.id, sublesson.id)
                                          }
                                        >
                                          <span className="theory-topic-subnum">
                                            {childIndex + 1}.
                                          </span>
                                          <span className="theory-topic-subtext">
                                            {sublesson.title}
                                          </span>
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="theory-suggest-card">
            <div className="theory-suggest-visual" aria-hidden>
              <img src="/home/hero-planet.png" alt="" className="theory-suggest-planet" />
            </div>
            <p className="theory-suggest-title">{copy.suggestTitle}</p>
            <p className="theory-suggest-text">{copy.suggestText}</p>
            <button
              type="button"
              className="theory-suggest-btn"
              onClick={() => setShowSuggestModal(true)}
            >
              {copy.suggestBtn}
              <FaArrowRight aria-hidden />
            </button>
          </div>
        </aside>

        <main className="theory-main">
          {leftNavCollapsed && (
            <button
              type="button"
              className="theory-nav-expand-btn"
              onClick={() => toggleLeftNav(false)}
              title={copy.expandNav}
              aria-label={copy.expandNav}
              aria-expanded={false}
            >
              <FaList aria-hidden />
              <span>{copy.libraryTitle}</span>
              <FaChevronRight className="theory-nav-expand-chevron" aria-hidden />
            </button>
          )}

          <header className="theory-article-head">
            {selection ? (
              <nav className="theory-breadcrumbs" aria-label="Breadcrumb">
                <span>{copy.breadcrumbTheory}</span>
                <span className="theory-breadcrumb-sep" aria-hidden>
                  /
                </span>
                <span>{selection.category.title}</span>
                <span className="theory-breadcrumb-sep" aria-hidden>
                  /
                </span>
                <span className="theory-breadcrumb-current">
                  {selection.sublesson?.title ?? selection.lesson.title}
                </span>
              </nav>
            ) : (
              <nav className="theory-breadcrumbs" aria-label="Breadcrumb">
                <span>{copy.breadcrumbTheory}</span>
              </nav>
            )}

            <h1 className="theory-article-title">{articleTitle}</h1>
            <p className="theory-article-desc">{articleSubtitle}</p>

            <div className="theory-article-actions">
              <button
                type="button"
                className="theory-practice-btn"
                onClick={() => setShowPracticeModal(true)}
                disabled={activeLessonId == null}
              >
                {copy.goPractice}
              </button>
              <button
                type="button"
                className={`theory-bookmark-btn${bookmarked ? ' is-active' : ''}${
                  !isAuthenticated ? ' is-disabled' : ''
                }`}
                onClick={handleToggleBookmark}
                disabled={!isAuthenticated}
                title={bookmarkTitle}
                aria-label={bookmarkTitle}
                aria-pressed={bookmarked}
              >
                <FaBookmark aria-hidden />
              </button>
            </div>
          </header>

          {articleSections ? (
            <TheoryArticleBody sections={articleSections} />
          ) : (
            <div className="theory-article-empty" role="status">
              {copy.articleEmpty}
            </div>
          )}

          <footer className="theory-article-nav">
            <button
              type="button"
              className="theory-nav-btn theory-nav-btn--ghost"
              onClick={handlePrevTopic}
              disabled={!hasPrevSublesson}
            >
              ← {copy.prevTopic}
            </button>
            <button
              type="button"
              className="theory-nav-btn theory-nav-btn--ghost"
              onClick={handleBackToList}
            >
              {copy.backToList}
            </button>
            {showCompleteTopic ? (
              <button
                type="button"
                className="theory-nav-btn theory-nav-btn--primary"
                onClick={handleCompleteTopic}
              >
                {copy.completeTopic} →
              </button>
            ) : (
              <button
                type="button"
                className="theory-nav-btn theory-nav-btn--primary"
                onClick={handleNextTopic}
                disabled={!hasNextSublesson}
              >
                {copy.nextTopic} →
              </button>
            )}
          </footer>
        </main>

        <aside className="theory-sidebar theory-sidebar--right">
          <div className="theory-side-card">
            <h2 className="theory-side-title">{copy.lessonStructure}</h2>
            <div className="theory-progress-head">
              <span className="theory-progress-label">{copy.progress}</span>
              <span className="theory-progress-value">
                {lessonProgress} / {lessonTotal}
              </span>
            </div>
            <div className="theory-progress-track">
              <div className="theory-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <ol className="theory-step-list">
              {lessonSteps.map((step, index) => {
                const isCurrent = index === currentStepIndex;
                const isDone =
                  isLessonCompleted ||
                  (currentStepIndex >= 0 && index < currentStepIndex) ||
                  (furthestSublessonIndex >= 0 &&
                    index < furthestSublessonIndex &&
                    !isCurrent);
                return (
                  <li
                    key={step.id}
                    className={`theory-step-item${isDone ? ' theory-step-item--done' : ''}${
                      isCurrent ? ' theory-step-item--current' : ''
                    }`}
                  >
                    <span className="theory-step-marker" aria-hidden>
                      {isDone ? <FaCheck /> : <span className="theory-step-circle" />}
                    </span>
                    <span className="theory-step-text">{step.title}</span>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="theory-side-card">
            <h2 className="theory-side-title">{copy.keyFacts}</h2>
            <ul className="theory-facts-list">
              <li className="theory-fact-item">
                <span className="theory-fact-icon theory-fact-icon--gold">
                  <FaBolt aria-hidden />
                </span>
                <span>{copy.facts[0]}</span>
              </li>
              <li className="theory-fact-item">
                <span className="theory-fact-icon theory-fact-icon--gold">
                  <FaChartBar aria-hidden />
                </span>
                <span>{copy.facts[1]}</span>
              </li>
              <li className="theory-fact-item">
                <span className="theory-fact-icon theory-fact-icon--gold">
                  <FaExclamationCircle aria-hidden />
                </span>
                <span>{copy.facts[2]}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <SuggestTopicModal
        open={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        categories={suggestCategories}
        language={lang}
      />

      <TheoryPracticeModal
        open={showPracticeModal}
        onClose={() => {
          setShowPracticeModal(false);
          loadSolvedProblemIds();
        }}
        lessonId={activeLessonId}
        lessonTitle={selection?.lesson.title ?? ''}
        lang={lang}
      />
    </div>
  );
}

export default TheoryPage;
